<script setup lang="ts">
import { RouterLink } from 'vue-router';
import type { BlogPost } from '@shared/types';

defineProps<{
  post: BlogPost;
}>();

const formatDate = (dateString: string | null) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  });
};
</script>

<template>
  <article class="news-card">
    <RouterLink :to="`/aktuelles/${post.slug}`" class="news-card-link">
      <div class="news-card-image">
        <div v-if="post.featuredImage" class="news-card-img">
          <img :src="post.featuredImage" :alt="post.title" loading="lazy" />
        </div>
        <div v-else class="news-card-placeholder">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M3 15l4-4a2 2 0 012.828 0L15 16" />
            <path d="M14 14l1-1a2 2 0 012.828 0L21 16" />
            <circle cx="8.5" cy="8.5" r="1.5" />
          </svg>
        </div>
      </div>

      <div class="news-card-body">
        <time v-if="post.publishedAt" class="news-card-date">
          {{ formatDate(post.publishedAt) }}
        </time>

        <h3 class="news-card-title">{{ post.title }}</h3>

        <p v-if="post.excerpt" class="news-card-excerpt">
          {{ post.excerpt }}
        </p>

        <span class="news-card-action">
          Weiterlesen
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </span>
      </div>
    </RouterLink>
  </article>
</template>

<style scoped>
.news-card {
  background: var(--bg-card);
  border-radius: var(--radius-xl);
  border: 1px solid var(--border-color);
  overflow: hidden;
  transition: border-color var(--transition-normal);
}

.news-card:hover {
  border-color: var(--border-color-strong);
}

.news-card-link {
  display: flex;
  flex-direction: column;
  text-decoration: none;
  color: inherit;
  height: 100%;
}

.news-card-image {
  height: 200px;
  background: var(--bg-tertiary);
  overflow: hidden;
}

.news-card-img {
  width: 100%;
  height: 100%;
}

.news-card-img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--transition-slow);
}

.news-card:hover .news-card-img img {
  transform: scale(1.03);
}

.news-card-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary);
}

.news-card-body {
  padding: var(--spacing-6);
  display: flex;
  flex-direction: column;
  flex: 1;
}

.news-card-date {
  display: block;
  font-size: var(--font-size-xs);
  color: var(--text-tertiary);
  font-weight: var(--font-weight-medium);
  margin-bottom: var(--spacing-2);
}

.news-card-title {
  font-size: var(--font-size-lg);
  color: var(--text-primary);
  margin-bottom: var(--spacing-3);
  line-height: var(--line-height-snug);
  letter-spacing: var(--letter-spacing-tight);
}

.news-card-excerpt {
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  margin-bottom: var(--spacing-4);
  flex: 1;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-card-action {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-primary);
  transition: gap var(--transition-fast);
}

.news-card:hover .news-card-action {
  gap: var(--spacing-3);
}
</style>
